import React, { useCallback, useEffect, useState } from 'react';

import { AlertAnimation } from '@eika/alert-animation';
import { Button, LinkButton } from '@eika/button';
import { WarningCard } from '@eika/card';
import { useViewType, ViewType } from '@eika/hooks';
import { RadioButtons } from '@eika/radio-button';
import { DarkSkeleton } from '@eika/skeleton';

import { useRegulatoryCodes } from '../../../fetchHooks/useRegulatoryCodes';
import type { RegulatoryCode } from '../../../generated-types';
import useHandleCrossBorderReload from '../../../hooks/useHandleCrossBorderReload';
import useTranslation from '../../../hooks/useTranslation';
import { useSigningContext } from '../../../signingContext/signingContext';
import CenteredModalContent from '../../shared/CenteredModalContent/CenteredModalContent';
import { NavigationButtons } from '../../shared/NavigationButtons';
import DescriptionField from './DescriptionField';
import { CrossBorderPaymentActionType } from './form/actions';
import useFormContext from './form/useFormContext';

import './CategoryList.scss';

const CategoryList: React.FC = () => {
    const translated = useTranslation();
    const viewType = useViewType();
    const { state, handleChange } = useFormContext();
    const [selectedOption, setSelectedOption] = useState(state.regulatoryCode ?? '');
    const [optionError, setOptionError] = useState(false);
    const [descriptionError, setDescriptionError] = useState<string | undefined>();
    const [showMore, setShowMore] = useState(false);
    const { data: regulatoryCodes, isLoading, error, refetch } = useRegulatoryCodes();
    const [filteredRegulatoryCodes, setFilteredRegulatoryCodes] = useState<RegulatoryCode[]>();
    const { signingStatus } = useSigningContext();

    useHandleCrossBorderReload(signingStatus);

    const getFilteredRegulatoryCodes = useCallback(() => {
        if (showMore || !regulatoryCodes) {
            return regulatoryCodes;
        }
        const filteredRegulatoryCodes = regulatoryCodes.slice(0, 5);
        if (!selectedOption || filteredRegulatoryCodes.find((code) => code.code === selectedOption)) {
            return filteredRegulatoryCodes;
        }

        return [...regulatoryCodes.slice(0, 4), regulatoryCodes.find((code) => code.code === selectedOption)!];
    }, [regulatoryCodes, selectedOption, showMore]);

    useEffect(() => {
        setFilteredRegulatoryCodes(getFilteredRegulatoryCodes());
    }, [getFilteredRegulatoryCodes]);

    const validateOption = (): boolean => {
        if (selectedOption === '') {
            setOptionError(true);
            return false;
        }
        return true;
    };

    const validateDescription = (): boolean => {
        if (!state.paymentDescription) {
            setDescriptionError(translated.crossBorderPayment.description.descriptionError);
            return false;
        }
        return true;
    };

    const validate = (): boolean => {
        const isValidOption = validateOption();
        const isValidDescription = validateDescription();
        return isValidOption && isValidDescription;
    };

    if (isLoading) {
        return (
            <>
                <h2 className="category-list__title">{translated.crossBorderPayment.categoryList.title}</h2>
                <DarkSkeleton height={27} width="94%" />
                <span className="category-list__list-skeleton">
                    {Array.from(Array(5)).map((_, index) => (
                        <DarkSkeleton
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            className="category-list__list-element-skeleton"
                            height={22}
                            width={150}
                        />
                    ))}
                </span>
                <DarkSkeleton height={26} width={62} />
            </>
        );
    }

    if (error) {
        return (
            <CenteredModalContent>
                <WarningCard
                    className="cross-border-payment__country-warning-card"
                    title={translated.crossBorderPayment.newReceiver.errorCountryTitle}
                    isModal
                    icon={<AlertAnimation />}
                >
                    <p>{translated.crossBorderPayment.categoryList.error}</p>
                    <Button className="cross-border-payment__country-warning-retry" onClick={() => refetch()}>
                        {translated.tryAgain}
                    </Button>
                </WarningCard>
            </CenteredModalContent>
        );
    }

    return (
        <>
            <h2 className="category-list__title">{translated.crossBorderPayment.categoryList.title}</h2>
            {filteredRegulatoryCodes && (
                <RadioButtons
                    label={translated.crossBorderPayment.categoryList.label}
                    onChange={(value) => {
                        setSelectedOption(value);
                        handleChange({
                            type: CrossBorderPaymentActionType.SET_REGULATORY_CODE,
                            payload: value,
                        });
                        setOptionError(false);
                    }}
                    options={filteredRegulatoryCodes.map((category) => {
                        return { value: category.code, label: category.description };
                    })}
                    selectedOption={selectedOption}
                    required
                    errorMessage={optionError ? translated.crossBorderPayment.categoryList.errorMessage : undefined}
                />
            )}

            <LinkButton className="category-list__show-more-button" onClick={() => setShowMore(!showMore)}>
                {!showMore
                    ? translated.crossBorderPayment.categoryList.showMore
                    : translated.crossBorderPayment.categoryList.showLess}
            </LinkButton>
            <DescriptionField resetError={() => setDescriptionError(undefined)} error={descriptionError} />
            <NavigationButtons
                onClick={validate}
                centerOnSmallerWidth={viewType !== ViewType.mobile}
                nextUrl="oppsummering"
            />
        </>
    );
};

export default CategoryList;
